import { AddTask } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import _, { Dictionary } from "lodash";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import {
  AccordionViewList,
  CreateTaskItemModal,
  filterTaskItemsFn,
  SnackbarContent,
  sortTaskItemsFn,
  TaskItem,
  TaskItemListItemSkeleton,
  TaskItemsTable,
  useDrawer,
} from "src/components";
import {
  ClosedTaskItemStatuses,
  Links,
  TASK_ITEM_STATUSES,
  TaskItemsFilterByOption,
  UserTaskItemsGroupByOption,
} from "src/constants";
import { useSession } from "src/hooks";
import { TaskItemDto, useGetUserTaskItems, UserDto } from "src/http";
import { useUserImplementationSettings } from "../hooks";
import { UserTaskItemsFilterButton } from "./UserTaskItemsFilterButton";

const AccordionContainer = styled(Box)`
  border-style: dashed;
  border-color: ${({ theme }) => alpha(theme.palette.common.black, 0.3)};
  border-width: 1px;
  border-radius: 10px;
`;

interface Props {
  userGuid: string;
  isTableView: boolean;
}

const UserTaskItemsList = (props: Props) => {
  const { displayName: sessionUserDisplayName, userId: sessionUserGuid } = useSession();
  const { settings } = useUserImplementationSettings(props.userGuid);
  const { data, isLoading } = useGetUserTaskItems(props.userGuid);
  const { closeDrawer } = useDrawer();
  const [groupedTasks, setGroupedTasks] = useState<Dictionary<TaskItemDto[]> | undefined>(undefined);
  const [isCreateTaskItemModalOpen, setIsCreateTaskItemModalOpen] = useState(false);

  const groupByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case UserTaskItemsGroupByOption.None: {
          return "All Tasks";
        }
        case UserTaskItemsGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
        case UserTaskItemsGroupByOption.InFocus: {
          return item.owners.some((owner) => owner.user.userId === props.userGuid && owner.isFocused)
            ? "Tasks in Focus"
            : "All Other Tasks";
        }
        case UserTaskItemsGroupByOption.Owner: {
          if (item.owners.length === 0) {
            return "No Owner";
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        }
        case UserTaskItemsGroupByOption.PrimaryOwner: {
          if (item.primaryOwner == null) {
            return "No Primary Owner";
          }

          return item.primaryOwner.displayName;
        }
        case UserTaskItemsGroupByOption.Status: {
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        }
        case UserTaskItemsGroupByOption.Team: {
          return item.team?.name ?? "No Team";
        }
        default:
          return;
      }
    },
    [props.userGuid, settings.taskItemsGroupByOption]
  );

  const groupSortByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case UserTaskItemsGroupByOption.DueDate:
          return item.dueDate;
        case UserTaskItemsGroupByOption.InFocus:
          return !item.owners.some((owner) => owner.user.userId === props.userGuid && owner.isFocused);
        case UserTaskItemsGroupByOption.None:
          return;
        case UserTaskItemsGroupByOption.Owner:
          if (item.owners.length === 1 && item.owners[0].user.userId === sessionUserGuid) {
            return -1;
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        case UserTaskItemsGroupByOption.PrimaryOwner:
          if (item.primaryOwner?.userId === sessionUserGuid) {
            return -1;
          }

          return item.primaryOwner?.displayName;
        case UserTaskItemsGroupByOption.Status:
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.sortOrder;
        case UserTaskItemsGroupByOption.Team:
          return item.team?.name ?? "No Team";
        default:
          return;
      }
    },
    [props.userGuid, sessionUserGuid, settings.taskItemsGroupByOption]
  );

  const filterByFunction = useCallback(
    (item: TaskItemDto) => {
      return filterTaskItemsFn(settings.taskItemsFilterByOption, settings.taskItemsFilterByStatusOption, item);
    },
    [settings.taskItemsFilterByOption, settings.taskItemsFilterByStatusOption]
  );

  function sortByFunction(a: TaskItemDto, b: TaskItemDto) {
    return sortTaskItemsFn(settings.taskItemsSortByOption, settings.taskItemsSortByDirection, a, b);
  }

  useEffect(() => {
    if (data !== undefined) {
      const taskItems = _.groupBy(
        _.sortBy(data.taskItems.filter(filterByFunction), groupSortByFunction),
        groupByFunction
      );

      if (
        settings.taskItemsGroupByOption === UserTaskItemsGroupByOption.InFocus &&
        taskItems["Tasks in Focus"] === undefined
      ) {
        setGroupedTasks({ "All Tasks": taskItems["All Other Tasks"] ?? [] });
      } else {
        setGroupedTasks(taskItems);
      }
    }
  }, [data, filterByFunction, groupByFunction, groupSortByFunction, settings.taskItemsGroupByOption]);

  function handleOwnershipChanged(taskItemGuid: string, taskItemDescription: string, removedOwners: UserDto[]) {
    if (removedOwners.some((x) => x.userId === sessionUserGuid)) {
      enqueueSnackbar(
        <SnackbarContent
          title="Success"
          content={
            <>
              <span>Your ownership was removed from </span>
              <Link href={Links.TaskItemDetail(taskItemGuid)}>{taskItemDescription}</Link>
            </>
          }
        />,
        {
          variant: "success",
        }
      );

      // Close drawer incase it's open.
      closeDrawer();
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title={<Typography variant="h6">Tasks</Typography>}
          action={
            <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
              {props.userGuid === sessionUserGuid && (
                <Button
                  variant="tertiary"
                  disabled={isLoading}
                  startIcon={<AddTask />}
                  onClick={() => setIsCreateTaskItemModalOpen(true)}
                >
                  Add New Task
                </Button>
              )}
              <UserTaskItemsFilterButton userGuid={props.userGuid} disabled={isLoading} />
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {isLoading || groupedTasks === undefined ? (
            <AccordionContainer>
              <Stack spacing={0.5} sx={{ mx: 1, my: 0.5 }} divider={<Divider variant="dashed" />}>
                {[...Array(3)].map((_, i) => (
                  <TaskItemListItemSkeleton key={i} />
                ))}
              </Stack>
            </AccordionContainer>
          ) : (
            <Stack spacing={1}>
              {_.keys(groupedTasks).length === 0 ? (
                <Typography>Nothing here yet.</Typography>
              ) : (
                _.keys(groupedTasks).map((groupName) => (
                  <>
                    <AccordionContainer
                      key={groupName}
                      sx={{
                        mb: groupName === sessionUserDisplayName ? "20px !important" : undefined,
                      }}
                    >
                      <AccordionViewList
                        title={groupName}
                        isLoading={isLoading}
                        dataSource={groupedTasks[groupName]}
                        divider={<Divider variant="dashed" sx={{ mx: 1 }} />}
                        renderContent={
                          props.isTableView
                            ? (items) => (
                                <TaskItemsTable
                                  tableId={`userTaskItemsList-${props.userGuid}`}
                                  taskItems={items.sort(sortByFunction)}
                                  onExpandRowClicked={(row) => row.getToggleExpandedHandler()()}
                                  onOwnershipChanged={handleOwnershipChanged}
                                  enableActions={sessionUserGuid === props.userGuid}
                                />
                              )
                            : undefined
                        }
                        renderItem={(item) => (
                          <TaskItem
                            key={item.guid}
                            taskItem={item}
                            userCanEdit={item.userCanEdit && sessionUserGuid === props.userGuid}
                            showTeamChip
                            onOwnershipChanged={handleOwnershipChanged}
                            sx={{ px: 1 }}
                          />
                        )}
                        activeItemsListProps={{
                          activeItemsSortFunction: sortByFunction,
                        }}
                        closedItemsListProps={
                          settings.taskItemsFilterByOption !== TaskItemsFilterByOption.Status
                            ? {
                                closedItemsFilter: (taskItem) =>
                                  ClosedTaskItemStatuses.includes(taskItem.currentStatus),
                                closedItemsSortFunction: sortByFunction,
                              }
                            : undefined
                        }
                      />
                    </AccordionContainer>
                  </>
                ))
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <CreateTaskItemModal
        visible={isCreateTaskItemModalOpen}
        onCreated={() => setIsCreateTaskItemModalOpen(false)}
        onCancelled={() => setIsCreateTaskItemModalOpen(false)}
      />
    </>
  );
};

export { UserTaskItemsList };
